import { callAPI2 } from "../helper/apiUtils";
import {
  compressChatVideo,
  isVideoFile,
  prepareChatVideo,
} from "./chatVideo";

let videoClientSeq = 0;

export const nextArticleVideoClientId = () =>
  `av-${Date.now()}-${++videoClientSeq}`;

export const isLocalArticleVideo = (file) =>
  !!(
    file &&
    (file.isVideo || file.fileType === "video" || isVideoFile(file)) &&
    (file.local || file instanceof File || String(file.url || "").startsWith("blob:"))
  );

export const stampArticleVideo = (
  file,
  {
    clientId,
    processing = false,
    uploading = false,
    progress = 0,
    originalName,
  } = {}
) => {
  const name = originalName || file.originalName || file.attachedFileName || file.name;
  if (!file.url) file.url = URL.createObjectURL(file);
  file.fileType = "video";
  file.isVideo = true;
  file.local = true;
  file.fileUrl = file.url;
  file.originalName = name;
  file.attachedFileName = file.name || name;
  file.clientId = clientId || file.clientId || nextArticleVideoClientId();
  file.processing = processing;
  file.uploading = uploading;
  file.progress = progress;
  return file;
};

export const prepareArticleVideoForComposer = async (file) => {
  const prepared = await prepareChatVideo(file);
  if (!prepared.ok) return prepared;
  return {
    ok: true,
    file: stampArticleVideo(prepared.file, {
      processing: true,
      progress: 0,
    }),
    needsCompress: true,
  };
};

export const compressArticleVideo = async (file, { onProgress, isStale } = {}) => {
  const clientId = file.clientId;
  const originalName = file.originalName;
  const compressed = await compressChatVideo(file, {
    onProgress: (pct) => {
      if (isStale?.()) return;
      onProgress?.(pct);
    },
  });
  if (isStale?.()) return null;
  if (
    compressed !== file &&
    file.url &&
    String(file.url).startsWith("blob:") &&
    compressed.url !== file.url
  ) {
    try {
      URL.revokeObjectURL(file.url);
    } catch (_) {
      /* ignore */
    }
  }
  return stampArticleVideo(compressed, {
    clientId,
    originalName,
    processing: false,
    uploading: false,
    progress: 0,
  });
};

export const mergeArticleVideoMeta = (files, videoMeta) =>
  (files || []).map((file) => {
    const meta = file?.clientId && videoMeta ? videoMeta[file.clientId] : null;
    if (!meta) return file;
    file.processing = !!meta.processing;
    file.uploading = !!meta.uploading;
    file.progress = meta.progress;
    return file;
  });

export const articleHasProcessingVideo = (files, videoMeta) =>
  (files || []).some((file) => {
    if (!file) return false;
    if (file.processing) return true;
    return !!(file.clientId && videoMeta?.[file.clientId]?.processing);
  });

export const articleHasLocalVideo = (files) =>
  (files || []).some(isLocalArticleVideo);

export const postArticleFormData = async (url, formData, { onUploadProgress } = {}) => {
  const response = await callAPI2(
    url,
    {},
    "POST",
    formData,
    {
      timeout: 600000,
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      onUploadProgress,
    }
  );
  const data = response && response.data;
  if (response && response.status >= 400) {
    const message =
      (data && typeof data === "object" && data.message) ||
      `Request failed (${response.status})`;
    const err = new Error(message);
    err.status = response.status;
    throw err;
  }
  return data;
};
